
// Deployment trigger: 2026-04-24
import React, { useState } from 'react';
import { 
  auth, 
  db, 
  setPersistence, 
  browserLocalPersistence, 
  browserSessionPersistence 
} from '../firebase';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { Mail, Lock, User, Gamepad2, ArrowRight, Loader2, AlertCircle, CheckCircle2, ShieldAlert } from 'lucide-react';
import Turnstile from './Turnstile';
import { verifyTurnstile } from '../services/turnstileService';

// Public Turnstile site key (safe to expose). When unset, the captcha is
// simply not enforced, so the app keeps working until you configure it.
const TURNSTILE_SITE_KEY = (import.meta as any).env?.VITE_TURNSTILE_SITE_KEY as string | undefined;

const Auth: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [isForgot, setIsForgot] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [permissionError, setPermissionError] = useState(false);

  // Form State
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  // Cloudflare Turnstile
  const captchaEnabled = !!TURNSTILE_SITE_KEY;
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [turnstileKey, setTurnstileKey] = useState(0);

  // Tokens are single-use; force a fresh widget after each attempt.
  const resetCaptcha = () => {
    setCaptchaToken(null);
    setTurnstileKey(k => k + 1);
  };

  const handleError = (err: any) => {
    console.error("Auth Error:", err);
    setPermissionError(false);
    if (captchaEnabled) resetCaptcha();

    let message = err.message.replace('Firebase: ', '');
    
    if (err.code === 'permission-denied') {
      setPermissionError(true);
      message = "Database permissions restricted. Please update Firestore Rules.";
    } else if (err.code === 'auth/user-not-found' || err.message.includes('Username not found')) {
      message = "Account not found. If you just signed up, try using your full email.";
    } else if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
      message = "Incorrect email or password.";
    } else if (err.code === 'auth/email-already-in-use') {
      message = "This email is already registered.";
    }
    
    setError(message);
    setLoading(false);
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setPermissionError(false);

    // Gate the auth action behind a server-verified Turnstile token.
    if (captchaEnabled) {
      if (!captchaToken) {
        setError("Please complete the verification challenge.");
        setLoading(false);
        return;
      }
      try {
        await verifyTurnstile(captchaToken);
      } catch (err: any) {
        setError(err.message || "Verification failed. Please try again.");
        resetCaptcha();
        setLoading(false);
        return;
      }
    }

    try {
      await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);

      if (isLogin) {
        let loginEmail = emailOrUsername.trim();
        
        // If it looks like a username (no @), look up the email
        if (!loginEmail.includes('@')) {
          try {
            const userDoc = await getDoc(doc(db, 'usernames', loginEmail.toLowerCase()));
            if (!userDoc.exists()) {
              throw new Error("Username not found. Please try your full email address.");
            }
            loginEmail = userDoc.data().email;
          } catch (fsErr: any) {
            if (fsErr.code === 'permission-denied') {
              throw fsErr; // Catch in main try/catch
            }
            throw new Error("Could not verify username. Try using your email.");
          }
        }

        await signInWithEmailAndPassword(auth, loginEmail, password);
      } else {
        // Register flow
        if (username.length < 3) throw new Error("Username must be at least 3 characters.");
        
        const userCredential = await createUserWithEmailAndPassword(auth, email.trim(), password);
        await updateProfile(userCredential.user, { displayName: username.trim() });

        // These might fail if rules aren't set, but we catch it
        try {
          await setDoc(doc(db, 'usernames', username.toLowerCase().trim()), {
            email: email.toLowerCase().trim(),
            uid: userCredential.user.uid
          });

          await setDoc(doc(db, 'users', userCredential.user.uid), {
            username: username.trim(),
            email: email.toLowerCase().trim(),
            uid: userCredential.user.uid,
            createdAt: new Date().toISOString()
          });
        } catch (ruleErr) {
          console.warn("Registered but Firestore write failed. Profile will sync on login.");
        }
      }
    } catch (err: any) {
      handleError(err);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (captchaEnabled) {
      if (!captchaToken) {
        setError("Please complete the verification challenge.");
        setLoading(false);
        return;
      }
      try {
        await verifyTurnstile(captchaToken);
      } catch (err: any) {
        setError(err.message || "Verification failed. Please try again.");
        resetCaptcha();
        setLoading(false);
        return;
      }
    }

    try {
      await sendPasswordResetEmail(auth, email.trim());
      setSuccess("Reset link sent! Check your email.");
      resetCaptcha();
      setTimeout(() => setIsForgot(false), 3000);
    } catch (err: any) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  if (isForgot) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-[40px] p-8 sm:p-12 shadow-2xl relative">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-black text-slate-800 italic uppercase">Reset Password</h2>
          </div>
          <form onSubmit={handleForgot} className="space-y-6">
            <input
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full px-6 py-4 rounded-2xl border border-slate-200 font-bold outline-none"
              placeholder="Your email address"
            />
            {captchaEnabled && (
              <Turnstile
                key={turnstileKey}
                siteKey={TURNSTILE_SITE_KEY!}
                onVerify={setCaptchaToken}
                onExpire={() => setCaptchaToken(null)}
                onError={() => setCaptchaToken(null)}
              />
            )}
            <button type="submit" disabled={loading || (captchaEnabled && !captchaToken)} className="w-full bg-indigo-600 text-white py-5 rounded-3xl font-black uppercase shadow-xl disabled:opacity-50">Send Link</button>
            <button type="button" onClick={() => { resetCaptcha(); setError(null); setIsForgot(false); }} className="w-full text-slate-400 font-bold text-sm">Back to Login</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-600/20 blur-[120px] rounded-full" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-600/20 blur-[120px] rounded-full" />

      <div className="w-full max-w-md bg-white rounded-[40px] p-8 sm:p-12 shadow-2xl relative z-10">
        <div className="flex justify-center mb-8">
          <div className="bg-indigo-600 p-4 rounded-3xl shadow-xl shadow-indigo-200">
            <Gamepad2 className="w-10 h-10 text-white" />
          </div>
        </div>

        <div className="text-center mb-10">
          <h2 className="text-3xl font-black text-slate-800 italic uppercase tracking-tight">
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </h2>
        </div>

        {error && (
          <div className={`mb-6 p-4 rounded-2xl flex flex-col gap-2 animate-in slide-in-from-top-2 border ${permissionError ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-red-50 border-red-100 text-red-600'}`}>
            <div className="flex items-center gap-3 text-xs font-bold">
              {permissionError ? <ShieldAlert className="w-5 h-5 shrink-0" /> : <AlertCircle className="w-5 h-5 shrink-0" />}
              {error}
            </div>
            {permissionError && (
              <p className="text-[10px] font-medium leading-relaxed mt-1 opacity-80">
                Firebase is blocking username lookup. <strong>Try logging in with your email address</strong> instead, or update your Firestore rules.
              </p>
            )}
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-100 text-emerald-600 rounded-2xl flex items-center gap-3 text-xs font-bold animate-in slide-in-from-top-2">
            <CheckCircle2 className="w-5 h-5 shrink-0" />
            {success}
          </div>
        )}

        <form onSubmit={handleAuth} className="space-y-6">
          {isLogin ? (
            <div>
              <label className="text-[10px] font-black text-slate-500 mb-2 block uppercase tracking-widest px-1">Email or Username</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input 
                  type="text" 
                  required
                  value={emailOrUsername}
                  onChange={e => setEmailOrUsername(e.target.value)}
                  className="w-full pl-12 pr-6 py-4 rounded-2xl border border-slate-200 focus:border-indigo-500 outline-none transition-all font-bold"
                  placeholder="Username or Email"
                />
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="text-[10px] font-black text-slate-500 mb-2 block uppercase tracking-widest px-1">Choose Username</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input 
                    type="text" 
                    required
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    className="w-full pl-12 pr-6 py-4 rounded-2xl border border-slate-200 outline-none font-bold"
                    placeholder="e.g. mbui41"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-500 mb-2 block uppercase tracking-widest px-1">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input 
                    type="email" 
                    required
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="w-full pl-12 pr-6 py-4 rounded-2xl border border-slate-200 outline-none font-bold"
                    placeholder="john@example.com"
                  />
                </div>
              </div>
            </>
          )}

          <div>
            <div className="flex justify-between items-center mb-2 px-1">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Password</label>
              {isLogin && <button type="button" onClick={() => { resetCaptcha(); setError(null); setIsForgot(true); }} className="text-[10px] font-bold text-indigo-600">Forgot?</button>}
            </div>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input 
                type="password" 
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full pl-12 pr-6 py-4 rounded-2xl border border-slate-200 outline-none font-bold"
                placeholder="••••••••"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 px-1">
            <input 
              type="checkbox" 
              id="remember"
              checked={rememberMe}
              onChange={e => setRememberMe(e.target.checked)}
              className="w-5 h-5 text-indigo-600 rounded-lg border-slate-300 focus:ring-indigo-500 cursor-pointer"
            />
            <label htmlFor="remember" className="text-xs font-bold text-slate-600 cursor-pointer">Stay Signed In</label>
          </div>

          {captchaEnabled && (
            <Turnstile
              key={turnstileKey}
              siteKey={TURNSTILE_SITE_KEY!}
              onVerify={setCaptchaToken}
              onExpire={() => setCaptchaToken(null)}
              onError={() => setCaptchaToken(null)}
            />
          )}

          <button
            type="submit"
            disabled={loading || (captchaEnabled && !captchaToken)}
            className="w-full bg-indigo-600 text-white py-5 rounded-3xl font-black uppercase tracking-[0.2em] text-xs hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-100 flex items-center justify-center gap-3 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (isLogin ? 'Enter Platform' : 'Get Started')}
          </button>
        </form>

        <div className="mt-10 pt-8 border-t border-slate-100 text-center">
          <p className="text-slate-400 text-sm font-medium">
            {isLogin ? "New to Business Name?" : "Already a member?"}
            <button 
              onClick={() => {
                setIsLogin(!isLogin);
                setError(null);
                resetCaptcha();
              }}
              className="ml-2 text-indigo-600 font-bold hover:underline"
            >
              {isLogin ? 'Create Account' : 'Log In'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;
